// Calcolatore inverso: dal netto desiderato alla RAL da chiedere.
//
// Due pagine, come il calcolatore del netto: /calcolo-lordo-da-netto raccoglie
// i dati, /calcolo-lordo-da-netto/risultati li legge dalla query string (JSON
// in base64, stesso formato del resto del sito) e mostra il risultato. Lo stato
// vive nell'URL, quindi il permalink e la condivisione funzionano senza codice
// dedicato.
//
// La matematica sta altrove: js/nettoEngine.js per la catena RAL -> netto e
// js/solveRal.js per l'inversione.
import data from '/data/data.json';
import { initComuneAutocomplete, getComuneSelezionato } from '/js/addizionaliComunali.js';
import { computeNetto } from '/js/nettoEngine.js';
import { solveRal, proiettaNetto } from '/js/solveRal.js';
// I grafici (e con loro d3) si caricano solo sulla pagina dei risultati:
// il form usa lo stesso modulo e non deve pagarne il peso.

const percorso = window.location.pathname.replace(/\/+$/, '');
const isRisultati = percorso === '/calcolo-lordo-da-netto/risultati'
  || percorso === '/calcolo-lordo-da-netto/risultati/index.html';

const REGIONI = Object.keys(data.aliquote_regionali['2026']);

const numero = (v) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(Math.round(v));
const euro = (v) => `${numero(v)} €`;
// Un decimale, ma "40,0%" si scrive "40%".
const percento = (v) => `${v.toFixed(1).replace(/\.0$/, '').replace('.', ',')}%`;

const encodeParams = (obj) => btoa(encodeURIComponent(JSON.stringify(obj)));
const decodeParams = (s) => {
  try { return JSON.parse(decodeURIComponent(atob(s))); } catch { return null; }
};

const parseBool = (v) => v === '1' || v === 1 || v === true || v === 'true';
const bool = (v, predefinito) => (v === undefined || v === '' ? predefinito : parseBool(v));

const ETICHETTE_MODO = {
  busta: 'netto in busta, mese ordinario',
  medio: 'netto medio mensile',
  annuo: 'netto annuo',
};

// ---------------------------------------------------------------------------
// Parametri
// ---------------------------------------------------------------------------

// Dai campi del form all'oggetto da mettere in URL.
function raccogliParametri() {
  const obj = {};
  const set = (k, v) => { if (v !== undefined && v !== null && v !== '') obj[k] = v; };
  const val = (id) => document.getElementById(id)?.value || '';
  const checked = (id) => document.getElementById(id)?.checked ?? false;

  set('netto', val('netto-target'));
  set('modo', document.querySelector('input[name="modo"]:checked')?.value || 'busta');
  set('regione', val('regione'));
  set('mensilita', document.querySelector('input[name="mensilita"]:checked')?.value || '14');
  set('year', document.querySelector('input[name="year"]:checked')?.value || '2026');
  set('addizionaleComunale', val('addizionale-comunale'));

  const comune = getComuneSelezionato();
  if (comune) {
    set('comune', comune.nome);
    if (comune.esenzione > 0) set('comuneEsenzione', comune.esenzione);
    if (comune.scaglioni) obj.comuneScaglioni = comune.scaglioni;
  }

  set('alinpsDip', val('alinps-dip'));
  set('alinpsDat', val('alinps-dat'));
  set('altreDetrazioni', val('altre-detrazioni'));
  set('imponibileCervelli', val('imponibile-cervelli'));

  if (checked('figli-carico')) {
    set('numeroFigli', val('numero-figli'));
    set('numeroFigliDisabili', val('numero-figli-disabili'));
    set('percentualeFigli', val('percentuale-figli'));
  }
  if (checked('ascendente-carico')) set('numeroAscendenti', val('numero-ascendenti'));

  const flag = [
    ['cuneo-fiscale', 'cuneoFiscale'],
    ['esonero-contributivo', 'esoneroContributivo'],
    ['madre-lavoratrice', 'madreLavoratrice'],
    ['bonus-100', 'bonus100'],
    ['apprendistato', 'apprendistato'],
    ['dipendente-pubblico', 'dipendentePubblico'],
    ['maggiore-15', 'maggiore15'],
    ['modifica-inps', 'modificaInps'],
    ['coniuge-carico', 'coniugeCarico'],
    ['figli-carico', 'figliCarico'],
    ['ascendente-carico', 'ascendenteCarico'],
    ['rientro-cervelli', 'rientroCervelli'],
  ];
  for (const [id, nome] of flag) {
    const el = document.getElementById(id);
    if (el) obj[nome] = el.checked ? '1' : '0';
  }
  return obj;
}

// Dall'URL ai parametri di computeNetto/solveRal.
function leggiParametri() {
  const query = new URLSearchParams(window.location.search);
  const grezzi = query.has('d') ? decodeParams(query.get('d')) : Object.fromEntries(query);
  if (!grezzi) return null;

  const target = Math.round(parseFloat(grezzi.netto) || 0);
  if (target <= 0) return null;

  const modo = ETICHETTE_MODO[grezzi.modo] ? grezzi.modo : 'busta';

  return {
    target,
    modo,
    grezzi,
    params: {
      regione: grezzi.regione || '',
      mensilita: parseInt(grezzi.mensilita, 10) || 14,
      year: grezzi.year || '2026',
      addizionaleComunale: parseFloat(grezzi.addizionaleComunale) || 0,
      comuneEsenzione: parseFloat(grezzi.comuneEsenzione) || 0,
      comuneScaglioni: Array.isArray(grezzi.comuneScaglioni) ? grezzi.comuneScaglioni : null,
      alinpsDip: grezzi.alinpsDip !== undefined ? parseFloat(grezzi.alinpsDip) : undefined,
      alinpsDat: grezzi.alinpsDat !== undefined ? parseFloat(grezzi.alinpsDat) : undefined,
      altreDetrazioni: parseFloat(grezzi.altreDetrazioni) || 0,
      imponibileCervelli: parseFloat(grezzi.imponibileCervelli) || 1,
      // Un link scritto a mano puo' non contenere le spunte: in quel caso
      // valgono i default del form, non "tutto disattivato".
      cuneoFiscale: bool(grezzi.cuneoFiscale, true),
      bonus100Flag: bool(grezzi.bonus100, true),
      esoneroContributivo: bool(grezzi.esoneroContributivo, false),
      madreLavoratrice: bool(grezzi.madreLavoratrice, false),
      apprendistato: bool(grezzi.apprendistato, false),
      coniugeCarico: bool(grezzi.coniugeCarico, false),
      figliCarico: bool(grezzi.figliCarico, (parseInt(grezzi.numeroFigli, 10) || 0) > 0),
      numeroFigli: parseInt(grezzi.numeroFigli, 10) || 0,
      numeroFigliDisabili: parseInt(grezzi.numeroFigliDisabili, 10) || 0,
      percentualeFigli: parseFloat(grezzi.percentualeFigli) || 100,
      ascendenteCarico: bool(grezzi.ascendenteCarico, (parseInt(grezzi.numeroAscendenti, 10) || 0) > 0),
      numeroAscendenti: parseInt(grezzi.numeroAscendenti, 10) || 0,
      rientroCervelli: bool(grezzi.rientroCervelli, false),
    },
  };
}

// Parametri per il calcolatore del netto, cosi' il passaggio di consegne
// riapre lo stesso profilo con la RAL trovata.
function parametriPerCalcolatoreNetto(grezzi, ral) {
  const { netto, modo, ...resto } = grezzi;
  return encodeParams({ ...resto, ral: String(ral) });
}

// ---------------------------------------------------------------------------
// Pagina del form
// ---------------------------------------------------------------------------

function initForm() {
  const nettoInput = document.getElementById('netto-target');
  const regione = document.getElementById('regione');
  const bottone = document.getElementById('calcolaButton');
  const alinpsDip = document.getElementById('alinps-dip');
  const alinpsDat = document.getElementById('alinps-dat');
  const inpsBlock = document.getElementById('aliquote-inps');

  const modificaInps = document.getElementById('modifica-inps');
  const dipendentePubblico = document.getElementById('dipendente-pubblico');
  const apprendistato = document.getElementById('apprendistato');
  const maggiore15 = document.getElementById('maggiore-15');

  // Aliquote INPS derivate dalle spunte, con la stessa precedenza del
  // calcolatore del netto: modifica manuale, pubblico, apprendistato, oltre 15.
  // In ripristino da URL le aliquote sono gia' state scritte dai parametri
  // salvati: `mantieniValori` evita di sovrascriverle con quelle di profilo.
  const sincronizzaInps = ({ mantieniValori = false } = {}) => {
    const q = data.aliquote_inps;
    if (modificaInps?.checked) {
      inpsBlock?.classList.remove('hidden-block');
      if (dipendentePubblico) dipendentePubblico.checked = false;
      if (apprendistato) apprendistato.checked = false;
      if (maggiore15) maggiore15.checked = false;
    } else if (mantieniValori && alinpsDip?.value && alinpsDat?.value) {
      inpsBlock?.classList.add('hidden-block');
    } else {
      inpsBlock?.classList.add('hidden-block');
      let dip = q.lavoratore.regolare;
      let dat = q.azienda.regolare;
      if (dipendentePubblico?.checked) { dip = q.lavoratore.pubblico; dat = q.azienda.pubblico; }
      else if (apprendistato?.checked) { dip = q.lavoratore.apprendistato; dat = q.azienda.apprendistato; }
      else if (maggiore15?.checked) { dip = q.lavoratore.quindici_dipendenti; dat = q.azienda.regolare; }
      if (alinpsDip) alinpsDip.value = (dip * 100).toFixed(2);
      if (alinpsDat) alinpsDat.value = (dat * 100).toFixed(2);
    }
    aggiornaStatoBottone();
  };

  // Le quattro spunte sulle aliquote si escludono a vicenda.
  const esclusive = [modificaInps, dipendentePubblico, apprendistato, maggiore15];
  esclusive.forEach((el) => {
    el?.addEventListener('change', () => {
      if (el.checked) esclusive.forEach((altro) => { if (altro !== el) altro.checked = false; });
      sincronizzaInps();
    });
  });

  // Blocchi condizionali
  const mostraSe = (checkboxId, blockId) => {
    const checkbox = document.getElementById(checkboxId);
    const blocco = document.getElementById(blockId);
    const applica = () => blocco?.classList.toggle('hidden-block', !checkbox?.checked);
    checkbox?.addEventListener('change', () => { applica(); aggiornaStatoBottone(); });
    applica();
  };
  mostraSe('figli-carico', 'figli-dettagli');
  mostraSe('ascendente-carico', 'ascendenti-dettagli');
  mostraSe('rientro-cervelli', 'cervelli-percentage');

  // Le detrazioni per oneri contano solo per incapienza, fra 15.000 e 28.000
  // di imponibile: si mostra il campo solo quando la RAL stimata ci finisce.
  const aggiornaAltreDetrazioni = () => {
    const riga = document.getElementById('altre-detrazioni-row');
    if (!riga) return;
    const bonusAttivo = document.getElementById('bonus-100')?.checked;
    const target = parseFloat(nettoInput?.value) || 0;
    const mensilita = parseInt(document.querySelector('input[name="modo"]:checked')?.value === 'annuo'
      ? 1
      : document.querySelector('input[name="mensilita"]:checked')?.value, 10) || 14;
    const nettoAnnuoStimato = target * mensilita;
    // approssimazione volutamente grossolana: serve solo a decidere se mostrare
    // il campo, il calcolo vero avviene sulla pagina risultati
    const imponibileStimato = nettoAnnuoStimato * 1.15;
    riga.classList.toggle('hidden-block', !(bonusAttivo && imponibileStimato > 15000 && imponibileStimato <= 28000));
  };

  document.getElementById('bonus-100')?.addEventListener('change', aggiornaAltreDetrazioni);

  function aggiornaStatoBottone() {
    if (!bottone) return;
    const target = parseFloat(nettoInput?.value) || 0;
    const ok = target > 0 && Boolean(regione?.value);
    bottone.disabled = !ok;
  }

  nettoInput?.addEventListener('input', () => {
    if (parseFloat(nettoInput.value) < 0) nettoInput.value = '';
    aggiornaAltreDetrazioni();
    aggiornaStatoBottone();
  });
  regione?.addEventListener('change', aggiornaStatoBottone);
  document.querySelectorAll('input[name="mensilita"], input[name="modo"], input[name="year"]')
    .forEach((el) => el.addEventListener('change', () => { aggiornaAltreDetrazioni(); aggiornaStatoBottone(); }));

  bottone?.addEventListener('click', () => {
    const payload = encodeURIComponent(encodeParams(raccogliParametri()));
    // replaceState prima di andarsene: riscrive la voce di cronologia del form
    // con i dati dentro, cosi' il tasto indietro del browser ci riporta a un
    // URL da cui il form si puo' ricostruire anche quando la bfcache non tiene.
    window.history.replaceState({}, '', `/calcolo-lordo-da-netto?d=${payload}`);
    window.location.href = `/calcolo-lordo-da-netto/risultati?d=${payload}`;
  });

  // Il testo accanto a una spunta la attiva, ma l'icona del tooltip che sta
  // nella stessa label no: per questo la label non e' associata all'input.
  document.querySelectorAll('[data-toggle]').forEach((span) => {
    span.addEventListener('click', () => {
      const checkbox = document.getElementById(span.dataset.toggle);
      if (!checkbox) return;
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    });
  });

  // Ricostruisce il form da un URL con i parametri. I radio riemettono `change`
  // perche' pilotano UI dipendente; le checkbox no, perche' i loro handler fanno
  // mutua esclusione e distruggerebbero la combinazione appena ripristinata:
  // lo stato derivato si ricalcola dopo, esplicitamente.
  const applicaParametriAlForm = (query) => {
    const grezzi = query.has('d') ? decodeParams(query.get('d')) : Object.fromEntries(query);
    if (!grezzi) return false;

    const presente = (chiave) => grezzi[chiave] !== undefined && grezzi[chiave] !== '';
    const valore = (id, chiave) => {
      if (!presente(chiave)) return;
      const el = document.getElementById(id);
      if (el) el.value = grezzi[chiave];
    };
    const spunta = (id, chiave) => {
      if (!presente(chiave)) return;
      const el = document.getElementById(id);
      if (el) el.checked = parseBool(grezzi[chiave]);
    };
    const radio = (nome, chiave) => {
      if (!presente(chiave)) return;
      const el = document.querySelector(`input[name="${nome}"][value="${grezzi[chiave]}"]`);
      if (el) { el.checked = true; el.dispatchEvent(new Event('change')); }
    };

    valore('netto-target', 'netto');
    valore('regione', 'regione');
    valore('comune', 'comune');
    valore('addizionale-comunale', 'addizionaleComunale');
    valore('alinps-dip', 'alinpsDip');
    valore('alinps-dat', 'alinpsDat');
    valore('altre-detrazioni', 'altreDetrazioni');
    valore('imponibile-cervelli', 'imponibileCervelli');
    valore('numero-figli', 'numeroFigli');
    valore('numero-figli-disabili', 'numeroFigliDisabili');
    valore('percentuale-figli', 'percentualeFigli');
    valore('numero-ascendenti', 'numeroAscendenti');
    radio('modo', 'modo');
    radio('mensilita', 'mensilita');
    radio('year', 'year');

    [
      ['cuneo-fiscale', 'cuneoFiscale'], ['esonero-contributivo', 'esoneroContributivo'],
      ['madre-lavoratrice', 'madreLavoratrice'], ['bonus-100', 'bonus100'],
      ['apprendistato', 'apprendistato'], ['dipendente-pubblico', 'dipendentePubblico'],
      ['maggiore-15', 'maggiore15'], ['modifica-inps', 'modificaInps'],
      ['coniuge-carico', 'coniugeCarico'], ['figli-carico', 'figliCarico'],
      ['ascendente-carico', 'ascendenteCarico'], ['rientro-cervelli', 'rientroCervelli'],
    ].forEach(([id, chiave]) => spunta(id, chiave));

    ['figli-carico', 'ascendente-carico', 'rientro-cervelli'].forEach((id) => {
      document.getElementById(id)?.dispatchEvent(new Event('change'));
    });
    sincronizzaInps({ mantieniValori: true });
    aggiornaAltreDetrazioni();
    aggiornaStatoBottone();
    return true;
  };

  const query = new URLSearchParams(window.location.search);
  const ripristinato = [...query.keys()].length > 0 && applicaParametriAlForm(query);
  if (!ripristinato) sincronizzaInps();

  aggiornaAltreDetrazioni();
  aggiornaStatoBottone();
  // dopo il ripristino, cosi' l'autocomplete si aggancia a regione e comune gia' scritti
  initComuneAutocomplete();

  // Ritorno da bfcache: initComuneAutocomplete non viene rieseguito, quindi il
  // campo comune mostrerebbe il testo senza che il modulo lo riconosca, e
  // getComuneSelezionato() resterebbe nullo facendo sparire l'addizionale.
  window.addEventListener('pageshow', (evento) => {
    if (!evento.persisted) return;
    const q = new URLSearchParams(window.location.search);
    if ([...q.keys()].length === 0) return;
    applicaParametriAlForm(q);
    const comune = document.getElementById('comune');
    if (comune?.value) comune.dispatchEvent(new Event('change'));
  });
}

// ---------------------------------------------------------------------------
// Pagina dei risultati
// ---------------------------------------------------------------------------

const scrivi = (id, testo) => {
  const el = document.getElementById(id);
  if (el) el.textContent = testo;
};

// Il ridisegno dei grafici cambia a ogni ricalcolo: l'ascoltatore del resize si
// registra una volta sola e passa sempre dall'ultimo disegnatore buono.
let ridisegnaGrafici = null;
let resizeAgganciato = false;

// Risolve e disegna tutto leggendo l'URL corrente. Richiamabile: la modifica
// rapida riscrive l'URL con replaceState e poi ripassa di qui, senza ricaricare.
function rendiRisultati() {
  const lettura = leggiParametri();
  if (!lettura) {
    window.location.href = '/calcolo-lordo-da-netto';
    return;
  }

  const { target, modo, params, grezzi } = lettura;
  const esito = solveRal(data, params, { target, modo });

  document.getElementById('risultato-ok')?.removeAttribute('hidden');
  document.getElementById('nav-sezioni')?.removeAttribute('hidden');
  document.getElementById('fuori-portata-testo')?.setAttribute('hidden', '');

  if (esito.fuoriPortata) {
    document.getElementById('risultato-ok')?.setAttribute('hidden', '');
    document.getElementById('nav-sezioni')?.setAttribute('hidden', '');
    scrivi('titolo-risultato', 'Questo netto è fuori portata');
    scrivi('riepilogo-target', `${euro(target)} · ${ETICHETTE_MODO[modo]} · ${params.mensilita} mensilità`);
    const testo = document.getElementById('fuori-portata-testo');
    if (testo) {
      testo.hidden = false;
      testo.textContent = `Con questi parametri il netto più alto raggiungibile è ${euro(esito.nettoEffettivo)}: `
        + `${euro(target)} richiederebbero una RAL fuori dalla scala del calcolatore.`;
    }
    return;
  }

  const b = esito.breakdown;
  const mensilita = params.mensilita;

  // -- Intestazione ---------------------------------------------------------
  // Il titolo porta la domanda con dentro la cifra chiesta; il sottotitolo le
  // ipotesi del calcolo. La RAL trovata la dice l'hero, e non va ripetuta qui.
  scrivi('titolo-risultato',
    `Quanto chiedere per ${euro(target)} netti ${modo === 'annuo' ? "all'anno" : 'al mese'}`);
  const luogo = grezzi.comune ? `${grezzi.comune}, ${params.regione}` : params.regione;
  const ipotesi = [ETICHETTE_MODO[modo], luogo, `${mensilita} mensilità`];
  if (params.year !== '2026') ipotesi.push(`anno ${params.year}`);
  const riepilogo = ipotesi.join(' · ');
  scrivi('riepilogo-target', riepilogo.charAt(0).toUpperCase() + riepilogo.slice(1));

  // -- Hero -----------------------------------------------------------------
  scrivi('ral-richiesta', euro(esito.ral));
  scrivi('lordo-mensile', euro(esito.ral / mensilita));

  // -- Verifica -------------------------------------------------------------
  scrivi('netto-verificato', euro(esito.nettoEffettivo));
  // La riga di verifica compare sempre, anche quando il netto chiesto cade in un
  // buco fra due soglie: la conferma va scritta solo se il numero coincide,
  // altrimenti smentisce la nota qui sotto.
  scrivi('netto-verificato-conferma', esito.esatto ? ', esattamente quello che hai chiesto' : '');
  const notaEsattezza = document.getElementById('nota-esattezza');
  if (notaEsattezza) {
    if (esito.esatto) {
      notaEsattezza.hidden = true;
    } else {
      notaEsattezza.hidden = false;
      scrivi('nota-esattezza-testo',
        `Nessuna RAL produce esattamente ${euro(target)}: una soglia fiscale salta quel valore. `
        + `${euro(esito.ral)} è la prima RAL che lo supera, con ${euro(esito.nettoEffettivo)}.`);
    }
  }

  const plateauAmpiezza = esito.plateau.max - esito.plateau.min;
  const notaPlateau = document.getElementById('nota-plateau');
  if (notaPlateau) {
    if (plateauAmpiezza >= 5) {
      notaPlateau.hidden = false;
      scrivi('nota-plateau-testo',
        `Chiedere fino a ${euro(esito.plateau.max)} non cambia il netto: `
        + `tutte le RAL fra ${euro(esito.plateau.min)} e ${euro(esito.plateau.max)} danno ${euro(esito.nettoEffettivo)}. `
        + 'Sopra quella cifra il netto ricomincia a salire.');
    } else {
      notaPlateau.hidden = true;
    }
  }

  // -- Dettaglio ------------------------------------------------------------
  const righe = [
    ['det-ral', b.ral],
    ['det-inps', b.inpsDipendente],
    ['det-imponibile', b.imponibileIrpef],
    ['det-irpef-lorda', b.irpefLorda],
    ['det-detrazione-lavoro', b.detrazioneLavoro],
    ['det-detrazione-coniuge', b.detrazioneConiuge],
    ['det-detrazione-figli', b.detrazioneFigli],
    ['det-detrazione-ascendenti', b.detrazioneAscendenti],
    ['det-ulteriore-detrazione', b.ulterioreDetrazione],
    ['det-irpef-netta', b.irpefNetta],
    ['det-add-regionale', b.addizionaleRegionale],
    ['det-add-comunale', b.addizionaleComunale],
    ['det-bonus-100', b.bonus100],
    ['det-somma-integrativa', b.sommaIntegrativa],
    ['det-netto-annuo', b.nettoAnnuo],
  ];
  righe.forEach(([id, valore]) => scrivi(id, euro(valore)));

  scrivi('dettaglio-ral', euro(b.ral));

  // -- Composizione annua, accanto al donut ---------------------------------
  scrivi('comp-netto-annuo', euro(b.nettoAnnuo));
  scrivi('comp-inps', euro(b.inpsDipendente));
  scrivi('comp-irpef', euro(b.totaleIrpef));

  // Con 12 mensilita' non c'e' nulla da ripartire: la card sparisce.
  const sezioneMensilita = document.getElementById('sezione-mensilita');
  if (sezioneMensilita) {
    if (b.nettoMensilitaAggiuntiva === null) {
      sezioneMensilita.hidden = true;
    } else {
      sezioneMensilita.hidden = false;
      scrivi('comp-netto-medio', euro(b.nettoMensileMedio));
      scrivi('comp-ordinaria', euro(b.nettoMensilitaOrdinaria));
      scrivi('comp-aggiuntiva', euro(b.nettoMensilitaAggiuntiva));
      scrivi('label-mensilita-aggiuntiva', mensilita >= 14 ? '13ª e 14ª mensilità' : '13ª mensilità');
    }
  }

  // -- Costo azienda e aliquota effettiva -----------------------------------
  scrivi('det-inps-azienda', euro(b.inpsAzienda));
  scrivi('det-tfr', euro(b.trattamentoFineRapporto));
  scrivi('det-inail', euro(b.inail));
  scrivi('det-costo-azienda', euro(b.costoAzienda));
  scrivi('aliquota-effettiva', percento(100 * (1 - b.nettoAnnuo / b.ral)));
  scrivi('quota-trattenuta', euro(b.ral - b.nettoAnnuo));

  // -- Soglie ---------------------------------------------------------------
  const boxSoglie = document.getElementById('box-soglie');
  const listaSoglie = document.getElementById('lista-soglie');
  if (boxSoglie && listaSoglie) {
    if (!esito.soglie.length) {
      boxSoglie.hidden = true;
    } else {
      boxSoglie.hidden = false;
      listaSoglie.replaceChildren(...esito.soglie.map((s) => {
        const li = document.createElement('li');
        li.textContent = `Intorno a ${euro(s.ral)} di RAL il netto annuo scende di ${euro(s.perdita)}: ${s.cause.join('; ')}.`;
        return li;
      }));
    }
  }

  // -- Sensibilità: curva del netto intorno alla RAL trovata ----------------
  const FINESTRA = 8000;
  const PASSO = 50;
  const punti = [];
  for (let ral = Math.max(0, esito.ral - FINESTRA); ral <= esito.ral + FINESTRA; ral += PASSO) {
    const b2 = computeNetto(data, { ...params, ral });
    punti.push({ ral, netto: proiettaNetto(b2, modo), nettoAnnuo: b2.nettoAnnuo });
  }

  // Le soglie si cercano sempre sul netto annuo, con la stessa soglia di
  // rumore del solver: sul mensile un salto vero vale pochi euro e si
  // confonderebbe con l'arrotondamento. Anche l'etichetta e' annua, cosi'
  // combacia con quella dell'elenco piu' sotto.
  const soglieVisibili = [];
  for (let i = 1; i < punti.length; i++) {
    const perdita = punti[i - 1].nettoAnnuo - punti[i].nettoAnnuo;
    if (perdita >= 20) soglieVisibili.push({ ral: punti[i].ral, perdita });
  }

  const corpoSensibilita = document.getElementById('tabella-sensibilita');
  if (corpoSensibilita) {
    const scarti = [-2000, -1000, 0, 1000, 2000, 5000];
    corpoSensibilita.replaceChildren(...scarti.map((scarto) => {
      const ral = Math.max(0, esito.ral + scarto);
      const netto = proiettaNetto(computeNetto(data, { ...params, ral }), modo);
      const tr = document.createElement('tr');
      if (scarto === 0) tr.className = 'highlight';
      const differenza = netto - esito.nettoEffettivo;
      const segno = differenza > 0 ? '+' : differenza < 0 ? '−' : '';
      tr.innerHTML = `<td>${euro(ral)}</td><td>${euro(netto)}</td>`
        + `<td>${scarto === 0 ? 'la tua richiesta' : `${segno}${numero(Math.abs(differenza))} €`}</td>`;
      return tr;
    }));
  }

  // js/lordoDaNettoCharts.js e' SVG scritto a mano e non porta dipendenze:
  // curva, barre e donut si disegnano appena arriva. Il Sankey e' l'unico
  // grafico che ha davvero bisogno di d3, quindi js/employeeCharts.js (e i
  // suoi 90 kB) si carica dopo e solo per quello.
  const caricaGrafici = () => import('/js/lordoDaNettoCharts.js');
  const caricaSankey = () => import('/js/employeeCharts.js');

  if (esito.euroMarginale) {
    scrivi('resa-marginale', euro(esito.euroMarginale.nettoAnnuoInPiu));
    scrivi('resa-percentuale', percento(esito.euroMarginale.resa * 100));
  }

  // -- Margine di trattativa ------------------------------------------------
  scrivi('margine-5', euro(Math.round(esito.ral * 1.05 / 100) * 100));
  scrivi('margine-10', euro(Math.round(esito.ral * 1.10 / 100) * 100));

  // -- Passaggio al calcolatore del netto -----------------------------------
  const link = document.getElementById('link-dettaglio-netto');
  if (link) link.href = `/calcolo-stipendio-netto?d=${parametriPerCalcolatoreNetto(grezzi, esito.ral)}`;

  // -- Confronto fra regioni ------------------------------------------------
  // Ventuno inversioni complete: la tabella si riempie subito, il grafico
  // quando i moduli sono pronti.
  const calcolaRegioni = () => {
    const corpo = document.getElementById('tabella-regioni');
    if (!corpo) return null;
    const righe = REGIONI
      .map((reg) => {
        const r = solveRal(data, { ...params, regione: reg }, { target, modo });
        return r.fuoriPortata ? null : { regione: reg, ral: r.ral };
      })
      .filter(Boolean)
      .sort((a, b2) => a.ral - b2.ral);
    if (!righe.length) return null;

    const minima = righe[0].ral;
    corpo.replaceChildren(...righe.map((r) => {
      const tr = document.createElement('tr');
      if (r.regione === params.regione) tr.className = 'highlight';
      const scarto = r.ral - minima;
      tr.innerHTML = `<td>${r.regione}</td><td>${euro(r.ral)}</td>`
        + `<td>${scarto === 0 ? '—' : `+${numero(scarto)} €`}</td>`;
      return tr;
    }));

    scrivi('forbice-regionale', euro(righe[righe.length - 1].ral - minima));
    document.getElementById('box-regioni')?.removeAttribute('hidden');
    return righe;
  };

  const composizione = [
    { nome: 'Netto annuo', valore: b.nettoAnnuo },
    { nome: 'Contributi INPS', valore: b.inpsDipendente },
    { nome: 'Ritenute IRPEF', valore: b.totaleIrpef },
  ];

  const avvia = async () => {
    const righeRegioni = calcolaRegioni();
    const grafici = await caricaGrafici();

    ridisegnaGrafici = () => {
      grafici.disegnaDonut('donut-container', 'legend-container', composizione, b.ral);
      grafici.disegnaCurvaNetto('grafico-sensibilita', punti, esito.ral, soglieVisibili, ETICHETTE_MODO[modo]);
      if (righeRegioni) grafici.disegnaConfrontoRegioni('grafico-regioni', righeRegioni, params.regione);
    };

    ridisegnaGrafici();

    // I grafici sono disegnati su misura del contenitore: al resize vanno
    // rifatti. Un solo ascoltatore, anche dopo molti ricalcoli.
    if (!resizeAgganciato) {
      resizeAgganciato = true;
      let attesaResize = null;
      window.addEventListener('resize', () => {
        clearTimeout(attesaResize);
        attesaResize = setTimeout(() => ridisegnaGrafici?.(), 200);
      }, { passive: true });
    }

    const { drawSankeyChart } = await caricaSankey();
    drawSankeyChart(
      b.ral, b.inpsAzienda, b.trattamentoFineRapporto, b.inail,
      b.nettoAnnuo, b.inpsDipendente, b.totaleIrpef, b.sommaIntegrativa, b.bonus100,
    );
  };

  if ('requestIdleCallback' in window) requestIdleCallback(avvia, { timeout: 1500 });
  else setTimeout(avvia, 0);
}

// ---------------------------------------------------------------------------
// Modifica rapida
// ---------------------------------------------------------------------------

// Le spunte che la modale sa mostrare come chip. paramName e' la chiave nel
// payload, etichetta il testo della chip.
const FLAG_MODIFICABILI = [
  { paramName: 'cuneoFiscale', etichetta: 'Taglio cuneo fiscale' },
  { paramName: 'bonus100', etichetta: 'Bonus IRPEF 100 €' },
  { paramName: 'esoneroContributivo', etichetta: 'Esonero contributivo 2024' },
  { paramName: 'madreLavoratrice', etichetta: 'Madre lavoratrice' },
  { paramName: 'apprendistato', etichetta: 'Contratto di apprendistato' },
  { paramName: 'dipendentePubblico', etichetta: 'Dipendente pubblico' },
  { paramName: 'maggiore15', etichetta: 'Azienda con più di 15 dipendenti' },
  { paramName: 'modificaInps', etichetta: 'Modifica aliquote INPS' },
  { paramName: 'coniugeCarico', etichetta: 'Coniuge a carico' },
  { paramName: 'figliCarico', etichetta: 'Figli a carico (dai 21 anni)' },
  { paramName: 'ascendenteCarico', etichetta: 'Genitore o nonno convivente a carico' },
  { paramName: 'rientroCervelli', etichetta: 'Rientro dei cervelli' },
];

// Chiave sintetica nel payload con l'elenco delle chip da mostrare. Senza,
// togliere una spunta ne farebbe sparire la chip alla riapertura e non si
// potrebbe piu' riattivare.
const CHIAVE_CHIP = 'qeFlags';

function initModificaRapida() {
  const modale = document.getElementById('quickEditModal');
  const form = document.getElementById('quickEditForm');
  const apri = document.getElementById('backArrow');
  const chiudi = document.getElementById('quickEditClose');
  const contenitoreChip = document.getElementById('quick-edit-active-flags');
  const nessunaChip = document.getElementById('quick-edit-no-flags');
  const ausiliari = document.getElementById('quick-edit-aux-fields');
  const nettoInput = document.getElementById('quick-edit-netto');
  const alConfiguratore = document.getElementById('quickEditToConfigurator');
  if (!modale || !form || !apri) return;

  const SELETTORI_FUOCO = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  let chipVisibili = [];
  let scrollSalvato = 0;

  const grezziCorrenti = () => {
    const query = new URLSearchParams(window.location.search);
    const grezzi = query.has('d') ? decodeParams(query.get('d')) : Object.fromEntries(query);
    return grezzi ? { ...grezzi } : {};
  };

  const statoChip = () => Object.fromEntries(
    Array.from(contenitoreChip.querySelectorAll('input[data-quick-flag]'))
      .map((el) => [el.dataset.quickFlag, el.checked]),
  );

  const valoreCampo = (id) => document.getElementById(id)?.value;

  // Imponibile di massima dal target scritto nella modale, per decidere quali
  // campi mostrare mentre l'utente digita.
  const imponibileStimato = () => {
    const target = parseFloat(nettoInput?.value) || 0;
    const modo = document.querySelector('input[name="quick-edit-modo"]:checked')?.value || 'busta';
    const mensilita = modo === 'annuo'
      ? 1
      : parseInt(document.querySelector('input[name="quick-edit-mensilita"]:checked')?.value, 10) || 14;
    return target * mensilita * 1.15;
  };

  // Quali chip mostrare: quelle memorizzate nel payload se ci sono, altrimenti
  // le spunte oggi attive.
  const chipDaMostrare = (grezzi) => {
    const ammesse = new Set(FLAG_MODIFICABILI.map((f) => f.paramName));
    const memorizzate = String(grezzi[CHIAVE_CHIP] || '')
      .split(',').map((s) => s.trim()).filter((s) => ammesse.has(s));
    if (memorizzate.length) return [...new Set(memorizzate)];
    return FLAG_MODIFICABILI
      .filter((f) => parseBool(grezzi[f.paramName]))
      .map((f) => f.paramName);
  };

  // I campi che dipendono da una spunta compaiono solo se quella spunta e' su.
  const disegnaAusiliari = () => {
    const grezzi = grezziCorrenti();
    const attivi = statoChip();
    const acceso = (nome) => (nome in attivi ? attivi[nome] : parseBool(grezzi[nome]));
    const pezzi = [];

    if (acceso('modificaInps')) {
      pezzi.push(`<div class="quick-edit-grid">
        <div class="input-group quick-edit-input-group">
          <label for="quick-edit-alinps-dip">Aliquota INPS dipendente</label>
          <input type="number" id="quick-edit-alinps-dip" min="0" max="40" step="0.01" value="${grezzi.alinpsDip ?? ''}">
        </div>
        <div class="input-group quick-edit-input-group">
          <label for="quick-edit-alinps-dat">Aliquota INPS datore</label>
          <input type="number" id="quick-edit-alinps-dat" min="0" max="50" step="0.01" value="${grezzi.alinpsDat ?? ''}">
        </div>
      </div>`);
    }
    // Le detrazioni per oneri contano solo per incapienza, fra 15.000 e 28.000
    // di imponibile: fuori da quella fascia il campo non serve. Stessa stima
    // grossolana del form, il calcolo vero lo fa il solver.
    if (acceso('bonus100') && imponibileStimato() > 15000 && imponibileStimato() <= 28000) {
      pezzi.push(`<div class="input-group quick-edit-input-group">
        <label for="quick-edit-altre-detrazioni">Detrazioni per oneri</label>
        <input type="number" id="quick-edit-altre-detrazioni" min="0" step="100" value="${grezzi.altreDetrazioni ?? ''}">
      </div>`);
    }
    if (acceso('rientroCervelli')) {
      const scelta = String(grezzi.imponibileCervelli ?? '0.5');
      const opzioni = [['0.5', '50% (regime 2024)'], ['0.4', '40% (con figlio minore)'],
        ['0.3', '30% (regime precedente)'], ['0.1', '10% (regime precedente, sud)']];
      pezzi.push(`<div class="input-group quick-edit-input-group">
        <label for="quick-edit-imponibile-cervelli">Percentuale reddito imponibile</label>
        <select id="quick-edit-imponibile-cervelli">
          ${opzioni.map(([v, t]) => `<option value="${v}"${v === scelta ? ' selected' : ''}>${t}</option>`).join('')}
        </select>
      </div>`);
    }
    if (acceso('figliCarico')) {
      pezzi.push(`<div class="quick-edit-grid">
        <div class="input-group quick-edit-input-group">
          <label for="quick-edit-numero-figli">Figli dai 21 ai 29 anni</label>
          <input type="number" id="quick-edit-numero-figli" min="1" max="10" step="1" value="${grezzi.numeroFigli || 1}">
        </div>
        <div class="input-group quick-edit-input-group">
          <label for="quick-edit-numero-figli-disabili">Di cui con disabilità</label>
          <input type="number" id="quick-edit-numero-figli-disabili" min="0" max="10" step="1" value="${grezzi.numeroFigliDisabili || 0}">
        </div>
      </div>`);
    }
    if (acceso('ascendenteCarico')) {
      pezzi.push(`<div class="input-group quick-edit-input-group">
        <label for="quick-edit-numero-ascendenti">Ascendenti conviventi a carico</label>
        <input type="number" id="quick-edit-numero-ascendenti" min="1" max="6" step="1" value="${grezzi.numeroAscendenti || 1}">
      </div>`);
    }
    ausiliari.innerHTML = pezzi.join('');
  };

  const disegnaChip = () => {
    const grezzi = grezziCorrenti();
    chipVisibili = chipDaMostrare(grezzi);
    const perNome = new Map(FLAG_MODIFICABILI.map((f) => [f.paramName, f]));

    contenitoreChip.innerHTML = chipVisibili.map((nome) => {
      const config = perNome.get(nome);
      if (!config) return '';
      return `<label class="inline-checkbox quick-edit-flag">
        <input type="checkbox" data-quick-flag="${nome}"${parseBool(grezzi[nome]) ? ' checked' : ''}>
        <span>${config.etichetta}</span>
      </label>`;
    }).join('');

    nessunaChip?.classList.toggle('hidden-block', chipVisibili.length > 0);
    contenitoreChip.querySelectorAll('input[data-quick-flag]')
      .forEach((el) => el.addEventListener('change', disegnaAusiliari));
    disegnaAusiliari();
  };

  const apriModale = () => {
    const grezzi = grezziCorrenti();
    if (nettoInput) nettoInput.value = Math.round(parseFloat(grezzi.netto) || 0);
    const modo = ETICHETTE_MODO[grezzi.modo] ? grezzi.modo : 'busta';
    document.querySelectorAll('input[name="quick-edit-modo"]')
      .forEach((el) => { el.checked = el.value === modo; });
    const mensilita = String(grezzi.mensilita || '14');
    document.querySelectorAll('input[name="quick-edit-mensilita"]')
      .forEach((el) => { el.checked = el.value === mensilita; });
    disegnaChip();

    modale.classList.add('is-open');
    modale.setAttribute('aria-hidden', 'false');
    scrollSalvato = window.scrollY || 0;
    document.documentElement.classList.add('modal-open');
    document.body.classList.add('modal-open');
    Object.assign(document.body.style, {
      position: 'fixed', top: `-${scrollSalvato}px`, left: '0', right: '0', width: '100%',
    });
    // L'originale non porta il fuoco dentro: qui si', altrimenti da tastiera la
    // modale si apre e il fuoco resta sulla pagina sotto.
    (nettoInput || chiudi)?.focus();
  };

  const chiudiModale = () => {
    modale.classList.remove('is-open');
    modale.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('modal-open');
    document.body.classList.remove('modal-open');
    ['position', 'top', 'left', 'right', 'width'].forEach((p) => { document.body.style[p] = ''; });
    window.scrollTo(0, scrollSalvato);
    apri.focus();
  };

  // Cambiare target, modo o mensilita' sposta la fascia di reddito, quindi
  // cambia quali campi ausiliari hanno senso.
  nettoInput?.addEventListener('input', disegnaAusiliari);
  document.querySelectorAll('input[name="quick-edit-modo"], input[name="quick-edit-mensilita"]')
    .forEach((el) => el.addEventListener('change', disegnaAusiliari));

  apri.addEventListener('click', apriModale);
  chiudi?.addEventListener('click', chiudiModale);
  modale.addEventListener('click', (e) => { if (e.target === modale) chiudiModale(); });

  document.addEventListener('keydown', (e) => {
    if (!modale.classList.contains('is-open')) return;
    if (e.key === 'Escape') { chiudiModale(); return; }
    if (e.key !== 'Tab') return;
    // Solo gli elementi davvero visibili: i campi ausiliari compaiono e
    // spariscono, e un elemento nascosto agli estremi romperebbe il ciclo.
    const fuocabili = Array.from(modale.querySelectorAll(SELETTORI_FUOCO))
      .filter((el) => !el.disabled && el.offsetParent !== null);
    if (!fuocabili.length) return;
    const primo = fuocabili[0];
    const ultimo = fuocabili[fuocabili.length - 1];
    if (e.shiftKey && document.activeElement === primo) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primo.focus(); }
  });

  // Riscrive il payload nell'URL e ritorna la query, senza toccare i parametri
  // che la modale non gestisce (regione, comune, anno...).
  const scriviPayload = () => {
    const grezzi = grezziCorrenti();
    const attivi = statoChip();

    grezzi.netto = String(Math.max(0, Math.round(parseFloat(nettoInput?.value) || 0)));
    grezzi.modo = document.querySelector('input[name="quick-edit-modo"]:checked')?.value || 'busta';
    grezzi.mensilita = document.querySelector('input[name="quick-edit-mensilita"]:checked')?.value || '14';
    grezzi[CHIAVE_CHIP] = chipVisibili.join(',');
    chipVisibili.forEach((nome) => { grezzi[nome] = attivi[nome] ? '1' : '0'; });

    // Set se acceso, delete se spento: spegnere una spunta ripulisce i suoi
    // parametri invece di lasciarli appesi nel payload.
    const acceso = (nome) => (nome in attivi ? attivi[nome] : parseBool(grezzi[nome]));
    const applica = (attivo, coppie) => coppie.forEach(([chiave, id]) => {
      const v = attivo ? valoreCampo(id) : undefined;
      if (v !== undefined && v !== '') grezzi[chiave] = v; else delete grezzi[chiave];
    });
    applica(acceso('modificaInps'), [['alinpsDip', 'quick-edit-alinps-dip'], ['alinpsDat', 'quick-edit-alinps-dat']]);
    applica(acceso('bonus100'), [['altreDetrazioni', 'quick-edit-altre-detrazioni']]);
    applica(acceso('rientroCervelli'), [['imponibileCervelli', 'quick-edit-imponibile-cervelli']]);
    applica(acceso('figliCarico'), [['numeroFigli', 'quick-edit-numero-figli'], ['numeroFigliDisabili', 'quick-edit-numero-figli-disabili']]);
    applica(acceso('ascendenteCarico'), [['numeroAscendenti', 'quick-edit-numero-ascendenti']]);

    const payload = encodeURIComponent(encodeParams(grezzi));
    window.history.replaceState({}, '', `/calcolo-lordo-da-netto/risultati?d=${payload}`);
    return payload;
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    scriviPayload();
    rendiRisultati();
    chiudiModale();
    window.scrollTo(0, 0);
  });

  alConfiguratore?.addEventListener('click', () => {
    window.location.href = `/calcolo-lordo-da-netto?d=${scriviPayload()}`;
  });
}

// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  if (isRisultati) {
    rendiRisultati();
    initModificaRapida();
  } else {
    initForm();
  }
});
